import { gematriya, greg, HDate } from '@hebcal/core';
import rambamData from './rambam-data.json';

const cycle_1_len = 1017;
const cycle_3_len = 339;
const cycle_start_date = new Date(1984, 3, 29);
const cycle_start_abs = greg.greg2abs(cycle_start_date);

const first4verses = [
  ['1', '2', '3'],
  ['1', '84', '248'],
  ['1', '123', '365'],
  ['1', '5', '14'],
];

const seferNames = Object.keys(rambamData);
const mishnehTorah = seferNames.map((name) => ({ name, ch: rambamData[name].chapters }));

// --------------------------------------------------------------------------
// 1-chapter-a-day schedule (matches chabad.org exactly)
// --------------------------------------------------------------------------
// Mishneh Torah has one liturgical appendix beyond the 1017 numbered chapters:
// the Text of the Haggadah, attached to the end of Hilchot Chametz u'Matzah.
// chabad's "1 Chapter a Day" luach gives the Haggadah its own study day, and to
// keep the cycle exactly 1017 days long it bundles the two shortest nusach units
// (Confession + Grace After Meals) onto a single day. We model both so our
// schedule lines up with chabad day-for-day (3-chapter is unaffected: there the
// Haggadah is appended to the Chametz day without consuming a slot).
const HAGGADAH_SEFER = 'Leavened and Unleavened Bread';
const HAGGADAH_PEREK = 'Text of the Haggadah';
const HAGGADAH_PEREK_HEB = 'נוסח ההגדה';
const BUNDLE_SEFER = 'Prayer Blessing Text and their order'; // Confession (3) + Grace (4)

const oneChapterSchedule = buildOneChapterSchedule();

function buildOneChapterSchedule() {
  // Canonical units in reading order, with the Haggadah inserted after the last
  // chapter of Chametz u'Matzah.
  const units = [];
  mishnehTorah.forEach((s, seferIdx) => {
    for (let c = 1; c <= s.ch; c++) {
      units.push({ seferIdx, name: s.name, chapterNum: c });
      if (s.name === HAGGADAH_SEFER && c === s.ch) {
        units.push({ seferIdx, name: s.name, haggadah: true });
      }
    }
  });

  // Group units into day-slots, bundling Confession (ch 3) + Grace (ch 4).
  const days = [];
  for (let i = 0; i < units.length; i++) {
    const u = units[i];
    const next = units[i + 1];
    if (u.name === BUNDLE_SEFER && u.chapterNum === 3 &&
        next && next.name === BUNDLE_SEFER && next.chapterNum === 4) {
      days.push([u, next]);
      i++; // Grace shares the day with Confession
    } else {
      days.push([u]);
    }
  }
  return days; // length === cycle_1_len (1017)
}

function toDisplayUnit(u) {
  if (u.haggadah) {
    const entry = rambamData[u.name];
    return {
      eng: { chapter_name: u.name, perek: HAGGADAH_PEREK },
      heb: { sefer: entry.hebrew, phonetic: entry.phonetic, perek: HAGGADAH_PEREK_HEB },
    };
  }
  const perek = u.seferIdx < 4 ? first4verses[u.seferIdx][u.chapterNum - 1] : u.chapterNum;
  return { eng: { chapter_name: u.name, perek }, heb: getHebrewText(u.name, perek) };
}

// Returns an array of the unit(s) studied on `date` in the 1-chapter cycle.
// Almost always one unit; two on the day chabad bundles Confession + Grace.
export function rambam_1_chapter(date) {
  const absDate = getAbsDate(date);
  checkTooEarly(absDate, cycle_start_abs, 'Rambam 1-chapter');
  const dno = ((absDate - cycle_start_abs) % cycle_1_len + cycle_1_len) % cycle_1_len;
  return oneChapterSchedule[dno].map(toDisplayUnit);
}

export function rambam_3_chapter(date) {
  const absDate = getAbsDate(date);
  checkTooEarly(absDate, cycle_start_abs, 'Rambam 3-chapter');
  const dno = (absDate - cycle_start_abs) % cycle_3_len;
  let total = dno * 3;
  let perakim = [];

  for (let j = 0; j < mishnehTorah.length; j++) {
    while (perakim.length < 3 && total < mishnehTorah[j].ch) {
      let chapNum = total + 1;
      let perek = j < 4 ? first4verses[j][chapNum - 1] : chapNum;
      let hebrew_text = getHebrewText(mishnehTorah[j].name, perek);
      perakim.push({ eng: { chapter_name: mishnehTorah[j].name, perek }, heb: hebrew_text });
      total++;
    }
    if (perakim.length === 3) return perakim;
    total -= mishnehTorah[j].ch;
  }

  throw new Error('Internal error, this code should be unreachable');
}

function getHebrewText(name, perek) {
  const entry = rambamData[name];
  return {
    sefer: entry.hebrew,
    phonetic: entry.phonetic,
    perek: gematriya(perek),
  };
}

function throwTypeError(msg) {
  throw new TypeError(msg);
}

export function getAbsDate(date) {
  const abs = (typeof date === 'number' && !isNaN(date)) ? date :
    greg.isDate(date) ? greg.greg2abs(date) :
    HDate.isHDate(date) ? date.abs() :
    throwTypeError(`Invalid date: ${date}`);
  return abs;
}

export function checkTooEarly(abs, startAbs, name) {
  if (abs < startAbs) {
    const dt = greg.abs2greg(abs);
    const dateStr = dt.toISOString().substring(0, 10);
    const startDt = greg.abs2greg(startAbs);
    const startDateStr = startDt.toISOString().substring(0, 10);
    throw new RangeError(`Date ${dateStr} too early; ${name} cycle began on ${startDateStr}`);
  }
}
